// ================================================================
// src/core/books/storage.js
// ================================================================
// لایه‌ی ذخیره‌سازی خالص سیستم کتاب‌های داینامیک
// بدون وابستگی به React — قابل استفاده در هر جای وب اپ
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. localStorage wrapper ایمن با مدیریت Quota
//   ۲. IndexedDB برای Blob ویدیو/تصویر با Chunking
//   ۳. Blob Manager: dedup, refCount, GC
//   ۴. Quota Manager: monitor, eviction
//   ۵. Event Bus داخلی برای pub/sub
//   ۶. Multi-Tab Sync با BroadcastChannel + fallback
//   ۷. Atomic Transactions با snapshot/rollback
//   ۸. Backup & Restore
//   ۹. Hashing (SHA-256) برای dedup
// ================================================================

// ================================================================
// SECTION 1: CONSTANTS & CONFIG
// ================================================================

const NAMESPACE = 'edu-books';
const SCHEMA_VERSION = '1.0.0';

// 🆕 دامنه‌ی آپلود فعلی — از UI ست می‌شود تا فایل‌ها زیر
//     media/exams/{ownerId}/{examId}/{type}/file.ext ذخیره شوند
//     و در زمان حذف پروژه قابل پاکسازی باشند.
export const uploadScope = {
  ownerId: null,   // شناسه‌ی معلم (tp_xxx)
  examId: null,    // شناسه‌ی پروژه (pj_xxx) یا 'profile'
};

// محدودیت‌ها
const LIMITS = Object.freeze({
  LOCALSTORAGE_SOFT: 4 * 1024 * 1024,          // 4MB — از این به بعد eviction
  LOCALSTORAGE_HARD: 4.5 * 1024 * 1024,        // 4.5MB — بلاک نوشتن جدید
  INDEXEDDB_SOFT: 400 * 1024 * 1024,           // 400MB
  INDEXEDDB_HARD: 480 * 1024 * 1024,           // 480MB
  MAX_VIDEO_SIZE: 500 * 1024 * 1024,           // 500MB per video (هماهنگ با سرور)
  MAX_IMAGE_SIZE: 5 * 1024 * 1024,             // 5MB per image
  MAX_AVATAR_SIZE: 2 * 1024 * 1024,            // 2MB
  CHUNK_SIZE: 5 * 1024 * 1024,                 // 5MB chunks
  BACKUP_KEEP_COUNT: 7,                        // 7 نسخه آخر
  BACKUP_KEEP_COUNT_LOW_SPACE: 3,              // وقتی فضا کمه
  ORPHAN_TTL_DAYS: 90,                         // ۹۰ روز نگه‌داری orphan
  AUDIT_LOG_TTL_DAYS: 30,                      // ۳۰ روز
  NOTIFICATION_TTL_DAYS: 30,                   // ۳۰ روز
  GC_BLOB_MIN_AGE_MS: 7 * 24 * 60 * 60 * 1000, // ۷ روز قبل از حذف blob بی‌استفاده
  URL_CACHE_MAX: 8,                            // حداکثر URL.createObjectURL کش‌شده
  SYNC_CHANNEL: 'edu-books-sync',
  IDB_NAME: 'edu_media_v1',
  IDB_VERSION: 1,
});

// نام ObjectStoreها
const IDB_STORES = Object.freeze({
  BLOBS: 'blobs',
  BLOB_META: 'blobMeta',
  BLOB_REFS: 'blobRefs',
  UPLOADS: 'uploads', // برای chunked upload resume
  META: 'meta',
});

// کلیدهای localStorage
export const KEYS = Object.freeze({
  // موجودیت‌ها
  TEACHERS: `${NAMESPACE}:teachers`,                        // map: teacherId → TeacherProfile
  TEACHER_BY_USER: `${NAMESPACE}:teacher-by-user`,          // map: userId → teacherId
  TEACHER_BY_FOLDER: `${NAMESPACE}:teacher-by-folder`,      // map: folderName → teacherId
  REQUESTS: `${NAMESPACE}:requests`,                        // map: requestId → TeacherRequest
  REQUEST_IDEMPOTENCY: `${NAMESPACE}:req-idem`,             // map: idempotencyKey → requestId
  PROJECTS: `${NAMESPACE}:projects`,                        // map: projectId → Project
  PROJECT_CATEGORIES: `${NAMESPACE}:project-categories`, // 🆕
  PROJECT_BY_TEACHER: `${NAMESPACE}:proj-by-teacher`,       // map: teacherId → projectId[]
  PROJECT_BY_KEY: `${NAMESPACE}:proj-by-key`,               // map: grade-subject-folder → projectId
  VERSIONS: `${NAMESPACE}:versions`,                        // map: versionId → ProjectVersion
  AUDIT_LOG: `${NAMESPACE}:audit`,                          // array of AuditLogEntry
  NOTIFICATIONS: `${NAMESPACE}:notifs`,                     // map: userId → Notification[]
  BACKUPS: `${NAMESPACE}:backups`,                          // array of BackupEntry
  USER_PREFS: `${NAMESPACE}:user-prefs`,                    // map: userId → prefs
  MIGRATION_FLAG: `${NAMESPACE}:migrated-v${SCHEMA_VERSION}`,
  LAST_GC: `${NAMESPACE}:last-gc`,
  LAST_CLEANUP: `${NAMESPACE}:last-cleanup`,
  SCHEMA: `${NAMESPACE}:schema`,
  // dynamic prefixes
  PROGRESS_PREFIX: 'lesson-progress-',   // سازگاری با کد فعلی
  RECORDS_PREFIX: 'lesson-records-',
  TEACHER_MAP_PREFIX: 'teacher-map-',
  FREESTUDY_PREFIX: 'freestudy-progress-',
  STEP_QUIZ_PROGRESS_PREFIX: 'step-quiz-progress-',
  STEP_QUIZ_UNLOCK_PREFIX: 'step-quiz-unlock-',
  STEP_QUIZ_PASS_PREFIX: 'step-quiz-pass-',
  // 🆕 کلیدهای اشتراک
  SUBSCRIPTION_PREFIX: 'subscription-',              // subscription-{userId}-{projectId}
  SUBSCRIPTION_BY_USER_PREFIX: 'subscriptions-user-',// subscriptions-user-{userId}
  SUBSCRIPTION_BY_PROJECT_PREFIX: 'subscriptions-project-', // subscriptions-project-{projectId}
  SUBSCRIPTION_TX_PREFIX: 'subscription-tx-',        // subscription-tx-{txId}
  SUBSCRIPTION_SALT: `${NAMESPACE}:sub-salt`,
});

// ================================================================
// SECTION 2: UTILITY FUNCTIONS
// ================================================================

/** تولید UUID v4 */
function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch {}
  }
  // fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** تولید short ID برای شناسه‌های قابل خواندن */
function shortId(prefix = '') {
  const s = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  return prefix ? `${prefix}_${s}` : s;
}

/** SHA-256 hash برای dedup */
async function sha256(blob) {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    // fallback: hash سبک برای محیط‌های ناامن (فقط dedup تقریبی)
    return simpleHashFromBlob(blob);
  }
  try {
    const buf = await blob.arrayBuffer();
    const hashBuf = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return simpleHashFromBlob(blob);
  }
}

async function simpleHashFromBlob(blob) {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let h = 2166136261;
  // sample every N bytes برای سرعت
  const step = Math.max(1, Math.floor(bytes.length / 10000));
  for (let i = 0; i < bytes.length; i += step) {
    h ^= bytes[i];
    h = Math.imul(h, 16777619);
  }
  return `fnv_${(h >>> 0).toString(16)}_${blob.size}`;
}

/** تبدیل بایت به رشته‌ی خوانا */
function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

/** اسلیپ */
function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/** Debounce */
function debounce(fn, wait = 300) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/** Throttle */
function throttle(fn, wait = 300) {
  let last = 0;
  let timer;
  return function (...args) {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      clearTimeout(timer);
      last = now;
      fn.apply(this, args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
}

/** Safe JSON parse */
function safeParse(raw, fallback = null) {
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

/** Safe JSON stringify */
function safeStringify(obj) {
  try { return JSON.stringify(obj); } catch { return null; }
}

/** محاسبه‌ی حجم تقریبی یک رشته */
function estimateStringSize(str) {
  if (typeof str !== 'string') return 0;
  // UTF-16 → معمولا ۲ بایت per char ولی برای فارسی ~۲، برای ASCII ~۱
  return str.length * 2;
}

/** بررسی وجود localStorage */
function hasLocalStorage() {
  try {
    const k = '__test__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch { return false; }
}

/** بررسی وجود IndexedDB */
function hasIndexedDB() {
  try { return typeof indexedDB !== 'undefined' && indexedDB !== null; }
  catch { return false; }
}

/** بررسی حالت Private Browsing */
function isPrivateMode() {
  try {
    const test = '__private_test__';
    localStorage.setItem(test, '1');
    localStorage.removeItem(test);
    return false;
  } catch (e) {
    return true;
  }
}

// ================================================================
// SECTION 3: EVENT BUS (Internal Pub/Sub)
// ================================================================

class EventBus {
  constructor() { this.listeners = new Map(); }

  on(event, handler) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(handler);
    return () => this.off(event, handler);
  }

  off(event, handler) {
    const set = this.listeners.get(event);
    if (set) set.delete(handler);
  }

  emit(event, payload) {
    const set = this.listeners.get(event);
    if (!set) return;
    set.forEach((fn) => {
      try { fn(payload); } catch (e) { console.warn('[EventBus] listener error:', e); }
    });
  }

  clear() { this.listeners.clear(); }
}

// ================================================================
// SECTION 4: SAFE LOCALSTORAGE WRAPPER
// ================================================================

class LocalStore {
  constructor() {
    this.available = hasLocalStorage();
    this.memoryFallback = new Map(); // اگه localStorage نبود
    this.writeLock = Promise.resolve(); // serialize writes برای atomicity
    this._sizeCache = null;
  }

  _getRaw(key) {
    if (!this.available) return this.memoryFallback.get(key) ?? null;
    try { return localStorage.getItem(key); } catch { return null; }
  }

  _setRaw(key, value) {
    if (!this.available) { this.memoryFallback.set(key, value); return; }
    localStorage.setItem(key, value);
  }

  _removeRaw(key) {
    if (!this.available) { this.memoryFallback.delete(key); return; }
    try { localStorage.removeItem(key); } catch {}
  }

  /** خواندن با JSON parse */
  get(key, fallback = null) {
    const raw = this._getRaw(key);
    return safeParse(raw, fallback);
  }

  /** خواندن رشته‌ی خام */
  getRaw(key) { return this._getRaw(key); }

  /**
   * نوشتن با JSON.stringify و مدیریت Quota
   * @returns {{ ok: boolean, error?: string, freed?: number }}
   */
  set(key, value, { silent = false } = {}) {
    const serialized = safeStringify(value);
    if (serialized == null) {
      return { ok: false, error: 'serialize_failed' };
    }
    return this.setRaw(key, serialized, { silent });
  }

  /** نوشتن رشته‌ی خام */
  setRaw(key, str, { silent = false } = {}) {
    const size = estimateStringSize(str) + estimateStringSize(key);
    try {
      this._setRaw(key, str);
      this._sizeCache = null; // invalidate
      if (!silent) engine?.events?.emit('storage:write', { key, size });
      return { ok: true };
    } catch (e) {
      // QuotaExceeded
      if (e && (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014)) {
        // تلاش برای آزادسازی فضا
        const freed = engine?.quota?.emergencyFree(size);
        if (freed && freed >= size) {
          try {
            this._setRaw(key, str);
            this._sizeCache = null;
            return { ok: true, freed };
          } catch (e2) {
            return { ok: false, error: 'quota_exceeded', freed };
          }
        }
        return { ok: false, error: 'quota_exceeded', freed: freed || 0 };
      }
      return { ok: false, error: String(e?.message || e) };
    }
  }

  /** حذف */
  remove(key) {
    this._removeRaw(key);
    this._sizeCache = null;
  }

  /** لیست کلیدها */
  keys(prefix = null) {
    if (!this.available) {
      return Array.from(this.memoryFallback.keys()).filter((k) => !prefix || k.startsWith(prefix));
    }
    const out = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (!prefix || k.startsWith(prefix))) out.push(k);
      }
    } catch {}
    return out;
  }

  /** پاک کردن همه‌ی کلیدهای یک prefix */
  removePrefix(prefix) {
    const keys = this.keys(prefix);
    keys.forEach((k) => this.remove(k));
    return keys.length;
  }

  /** تخمین حجم کل localStorage */
  estimateSize() {
    if (this._sizeCache != null) return this._sizeCache;
    if (!this.available) {
      let total = 0;
      this.memoryFallback.forEach((v, k) => {
        total += estimateStringSize(v) + estimateStringSize(k);
      });
      this._sizeCache = total;
      return total;
    }
    let total = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        const v = localStorage.getItem(k) || '';
        total += estimateStringSize(v) + estimateStringSize(k);
      }
    } catch {}
    this._sizeCache = total;
    return total;
  }

  /** پاک کردن کل namespace */
  clearNamespace() {
    return this.removePrefix(`${NAMESPACE}:`);
  }

  // ---------------- Atomic Transactions ----------------

  /**
   * اجرای یک تراکنش اتمیک روی چند کلید
   * snapshot می‌گیرد، اجرا می‌کند، در صورت خطا rollback می‌کند
   */
  async transaction(fn) {
    const snapshot = new Map();
    const touched = new Set();

    const tx = {
      get: (k, fb) => this.get(k, fb),
      set: (k, v) => {
        if (!snapshot.has(k)) snapshot.set(k, this._getRaw(k));
        touched.add(k);
        return this.set(k, v, { silent: true });
      },
      setRaw: (k, v) => {
        if (!snapshot.has(k)) snapshot.set(k, this._getRaw(k));
        touched.add(k);
        return this.setRaw(k, v, { silent: true });
      },
      remove: (k) => {
        if (!snapshot.has(k)) snapshot.set(k, this._getRaw(k));
        touched.add(k);
        this.remove(k);
      },
    };

    try {
      const result = await fn(tx);
      this._sizeCache = null;
      return { ok: true, result };
    } catch (e) {
      // rollback
      snapshot.forEach((raw, k) => {
        if (raw == null) this.remove(k);
        else this._setRaw(k, raw);
      });
      this._sizeCache = null;
      return { ok: false, error: String(e?.message || e), rolledBack: true };
    }
  }
}

// ================================================================
// SECTION 5: INDEXEDDB WRAPPER (Blob Store)
// ================================================================

class BlobStore {
  constructor() {
    this.available = hasIndexedDB();
    this.db = null;
    this.dbPromise = null;
    this.memoryFallback = new Map(); // Map<blobId, {blob, meta}>
  }

  async _open() {
    if (this.dbPromise) return this.dbPromise;
    if (!this.available) return null;

    this.dbPromise = new Promise((resolve, reject) => {
      let req;
      try { req = indexedDB.open(LIMITS.IDB_NAME, LIMITS.IDB_VERSION); }
      catch (e) { reject(e); return; }

      req.onupgradeneeded = (ev) => {
        const db = ev.target.result;
        // blobs: خودِ Blob + meta
        if (!db.objectStoreNames.contains(IDB_STORES.BLOBS)) {
          const s = db.createObjectStore(IDB_STORES.BLOBS, { keyPath: 'blobId' });
          s.createIndex('uploadedAt', 'uploadedAt');
          s.createIndex('hash', 'hash');
        }
        // blobMeta: اطلاعات کم‌حجم برای سریع
        if (!db.objectStoreNames.contains(IDB_STORES.BLOB_META)) {
          const s = db.createObjectStore(IDB_STORES.BLOB_META, { keyPath: 'blobId' });
          s.createIndex('refCount', 'refCount');
          s.createIndex('uploadedAt', 'uploadedAt');
        }
        // blobRefs: نگاشت (refType, refId) → blobId برای GC
        if (!db.objectStoreNames.contains(IDB_STORES.BLOB_REFS)) {
          const s = db.createObjectStore(IDB_STORES.BLOB_REFS, { keyPath: ['refType', 'refId', 'blobId'] });
          s.createIndex('blobId', 'blobId');
          s.createIndex('refId', 'refId');
        }
        // uploads: برای chunked upload resume
        if (!db.objectStoreNames.contains(IDB_STORES.UPLOADS)) {
          db.createObjectStore(IDB_STORES.UPLOADS, { keyPath: 'uploadId' });
        }
        // meta: key/value عمومی
        if (!db.objectStoreNames.contains(IDB_STORES.META)) {
          db.createObjectStore(IDB_STORES.META, { keyPath: 'key' });
        }
      };

      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => {
        // وقتی تب دیگه IDB رو باز کرده
        reject(new Error('idb_blocked'));
      };
    });

    try {
      this.db = await this.dbPromise;
      return this.db;
    } catch (e) {
      console.warn('[BlobStore] IDB open failed, using memory fallback:', e);
      this.available = false;
      return null;
    }
  }

  async _tx(storeNames, mode, fn) {
    const db = await this._open();
    if (!db) throw new Error('idb_unavailable');
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeNames, mode);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('tx_aborted'));
      let result;
      try { result = fn(tx); } catch (e) { reject(e); return; }
      tx.oncomplete = () => resolve(result);
    });
  }

  async _get(storeName, key) {
    if (!this.available) return this.memoryFallback.get(key) || null;
    const db = await this._open();
    if (!db) return this.memoryFallback.get(key) || null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  async _put(storeName, value) {
    if (!this.available) { this.memoryFallback.set(value.blobId || value.key, value); return; }
    const db = await this._open();
    if (!db) { this.memoryFallback.set(value.blobId || value.key, value); return; }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).put(value);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async _delete(storeName, key) {
    if (!this.available) { this.memoryFallback.delete(key); return; }
    const db = await this._open();
    if (!db) { this.memoryFallback.delete(key); return; }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async _getAll(storeName) {
    if (!this.available) return Array.from(this.memoryFallback.values());
    const db = await this._open();
    if (!db) return Array.from(this.memoryFallback.values());
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async _getAllByIndex(storeName, indexName, value) {
    if (!this.available) {
      return Array.from(this.memoryFallback.values()).filter((v) => v[indexName] === value);
    }
    const db = await this._open();
    if (!db) return [];
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).index(indexName).getAll(value);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }
}

// ================================================================
// SECTION 6: BLOB MANAGER
// ================================================================

class BlobManager {
  constructor(store, localStore, events) {
    this.store = store;
    this.local = localStore;
    this.events = events;
    this.urlCache = new Map(); // blobId → { url, lastUsed }
    this.uploadSessions = new Map(); // uploadId → state
  }

  /** URL cache LRU */
  _cacheUrl(blobId, url) {
    // اگه cache پر شده، قدیمی‌ترین رو revoke کن
    if (this.urlCache.size >= LIMITS.URL_CACHE_MAX) {
      let oldestKey = null;
      let oldestTime = Infinity;
      for (const [k, v] of this.urlCache) {
        if (v.lastUsed < oldestTime) { oldestTime = v.lastUsed; oldestKey = k; }
      }
      if (oldestKey) {
        try { URL.revokeObjectURL(this.urlCache.get(oldestKey).url); } catch {}
        this.urlCache.delete(oldestKey);
      }
    }
    this.urlCache.set(blobId, { url, lastUsed: Date.now() });
  }

  /** آزاد کردن URL از cache */
  releaseUrl(blobId) {
    const entry = this.urlCache.get(blobId);
    if (entry) {
      try { URL.revokeObjectURL(entry.url); } catch {}
      this.urlCache.delete(blobId);
    }
  }

  /** پاک کردن کل cache */
  clearUrlCache() {
    for (const { url } of this.urlCache.values()) {
      try { URL.revokeObjectURL(url); } catch {}
    }
    this.urlCache.clear();
  }

  /**
   * ذخیره‌ی Blob جدید
   * @param {Blob} blob
   * @param {object} meta - { mimeType, filename, refType, refId, title, duration, width, height }
   * @returns {Promise<{ blobId, hash, size, deduped }>}
   */
async save(blob, meta = {}) {
  const size = blob.size;

  if (meta.refType === 'video' && size > LIMITS.MAX_VIDEO_SIZE) {
    throw new Error(`video_too_large: max ${formatBytes(LIMITS.MAX_VIDEO_SIZE)}`);
  }
  if (meta.refType === 'image' && size > LIMITS.MAX_IMAGE_SIZE) {
    throw new Error(`image_too_large: max ${formatBytes(LIMITS.MAX_IMAGE_SIZE)}`);
  }
  if (meta.refType === 'avatar' && size > LIMITS.MAX_AVATAR_SIZE) {
    throw new Error(`avatar_too_large: max ${formatBytes(LIMITS.MAX_AVATAR_SIZE)}`);
  }

  // 🆕 دامنه‌ی مؤثر آپلود: اول meta، بعد scope، بعد fallback
  const effectiveOwnerId = meta.ownerId || uploadScope.ownerId || 'unknown';
  const effectiveExamId  = meta.examId  || uploadScope.examId  || `temp_${Date.now()}`;

  // 🆕 اول تلاش برای آپلود سرور
  try {
    const { uploadFile } = await import('./apiClient.js');
    
    // 🆕 تشخیص هوشمند پسوند و نوع فایل
    let mimeType = blob.type || meta.mimeType || '';
    let fileName = meta.filename || `file_${Date.now()}`;
    
    // استخراج پسوند از نام فایل
    let fileExt = '';
    if (fileName.includes('.')) {
      fileExt = fileName.split('.').pop().toLowerCase();
    } else if (mimeType) {
      fileExt = mimeType.split('/')[1] || 'bin';
      fileName = `${fileName}.${fileExt}`;
    }
    
    // 🆕 اگر MIME خالی یا نامشخص بود، حتماً از روی پسوند آن را تصحیح کن
    if (!mimeType || mimeType === 'application/octet-stream') {
      const extMap = {
        'mp4': 'video/mp4', 'webm': 'video/webm', 'ogg': 'video/ogg',
        'mov': 'video/quicktime', 'avi': 'video/x-msvideo', 'mkv': 'video/x-matroska',
        'png': 'image/png', 'jpg': 'image/jpeg', 'jpeg': 'image/jpeg',
        'gif': 'image/gif', 'webp': 'image/webp'
      };
      mimeType = extMap[fileExt] || 'application/octet-stream';
    }

    const file = new File([blob], fileName, { type: mimeType });
    
    const serverPath = await uploadFile(file, {
      teacherId: effectiveOwnerId,
      examId: effectiveExamId,
      type: meta.refType === 'video' ? 'file' : (meta.refType === 'avatar' ? 'cover' : 'question'),
      onProgress: meta.onProgress, // 🆕 پاس دادن درصد
      signal: meta.signal,         // 🆕 پاس دادن سیگنال لغو
    });

    const blobId = serverPath;
    const hash = `server_${serverPath}`;

    if (meta.refType && meta.refId) {
      try {
        await this.store._put(IDB_STORES.BLOB_REFS, {
          refType: meta.refType, refId: meta.refId, blobId, createdAt: Date.now(),
        });
      } catch {}
    }

    this.events.emit('blob:saved', { blobId, deduped: false, hash, size });
    return { blobId, hash, size, deduped: false, serverPath };
  } catch (e) {
    console.error('[BlobManager] ⚠️ server upload failed!', {
      error: e.message,
      refType: meta.refType,
      ownerId: effectiveOwnerId,
      examId: effectiveExamId,
    });
    try {
      this.events.emit('blob:server-upload-failed', {
        error: e.message,
        meta: { ...meta, ownerId: effectiveOwnerId, examId: effectiveExamId },
      });
    } catch {}
    
    // 🆕 اصلاح: پرتاب خطا. اگر سرور خطا دهد، فایل نباید به صورت ساکت در مرورگر ذخیره شود.
    // این کار باعث می‌شود کاربر پیام «خطا در آپلود» ببیند و از ایجاد داده‌ی یتیم جلوگیری شود.
    throw new Error(`server_upload_failed: ${e.message}`);
  }

  // ───────── fallback: IDB ─────────
  if (engine?.quota?.wouldExceedIDB(size)) {
    const freed = await engine.quota.emergencyFreeIDB(size);
    if (freed < size && engine.quota.isIDBCritical()) {
      throw new Error('idb_quota_exceeded');
    }
  }

  const hash = await sha256(blob);
  const existing = await this.store._getAllByIndex(IDB_STORES.BLOBS, 'hash', hash);
  if (existing && existing.length > 0) {
    const dup = existing[0];
    const metaRec = await this.store._get(IDB_STORES.BLOB_META, dup.blobId);
    if (metaRec) {
      metaRec.refCount = (metaRec.refCount || 1) + 1;
      metaRec.lastRefAt = Date.now();
      await this.store._put(IDB_STORES.BLOB_META, metaRec);
    }
    if (meta.refType && meta.refId) {
      await this.store._put(IDB_STORES.BLOB_REFS, {
        refType: meta.refType, refId: meta.refId, blobId: dup.blobId, createdAt: Date.now(),
      });
    }
    this.events.emit('blob:saved', { blobId: dup.blobId, deduped: true, hash });
    return { blobId: dup.blobId, hash, size: dup.size, deduped: true };
  }

  const blobId = `blob_${shortId()}`;
  const now = Date.now();
  await this.store._put(IDB_STORES.BLOBS, {
    blobId, blob, size,
    mimeType: meta.mimeType || blob.type || 'application/octet-stream',
    hash, uploadedAt: now,
  });
  await this.store._put(IDB_STORES.BLOB_META, {
    blobId, size,
    mimeType: meta.mimeType || blob.type || 'application/octet-stream',
    hash, title: meta.title || meta.filename || '',
    duration: meta.duration || null, width: meta.width || null, height: meta.height || null,
    refCount: 1, uploadedAt: now, lastRefAt: now, refType: meta.refType || null,
  });
  if (meta.refType && meta.refId) {
    await this.store._put(IDB_STORES.BLOB_REFS, {
      refType: meta.refType, refId: meta.refId, blobId, createdAt: now,
    });
  }
  this.events.emit('blob:saved', { blobId, deduped: false, hash, size });
  return { blobId, hash, size, deduped: false };
}

  /** خواندن Blob */
  async load(blobId) {
    const rec = await this.store._get(IDB_STORES.BLOBS, blobId);
    return rec?.blob || null;
  }

  /** خواندن meta */
  async loadMeta(blobId) {
    return this.store._get(IDB_STORES.BLOB_META, blobId);
  }

  /** ساخت URL برای پخش (با caching) */
async getUrl(blobId) {
  if (!blobId) return null;
  
  // 🆕 اصلاح: استفاده از FILE_API که در apiClient.js تشخیص داده می‌شود
  if (typeof blobId === 'string' && blobId.startsWith('/media/')) {
    try {
      const { FILE_API } = await import('./apiClient.js');
      return `${FILE_API}${blobId}`;
    } catch (e) {
      console.warn('[BlobManager] Failed to load FILE_API:', e);
      const base = process.env.REACT_APP_FILE_API_URL || '';
      return `${base}${blobId}`;
    }
  }
  const cached = this.urlCache.get(blobId);
  if (cached) {
    cached.lastUsed = Date.now();
    return cached.url;
  }
  const blob = await this.load(blobId);
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  this._cacheUrl(blobId, url);
  return url;
}

  /** افزودن ref جدید */
  async addRef(blobId, refType, refId) {
    const metaRec = await this.store._get(IDB_STORES.BLOB_META, blobId);
    if (!metaRec) return false;
    metaRec.refCount = (metaRec.refCount || 0) + 1;
    metaRec.lastRefAt = Date.now();
    await this.store._put(IDB_STORES.BLOB_META, metaRec);
    await this.store._put(IDB_STORES.BLOB_REFS, {
      refType, refId, blobId, createdAt: Date.now(),
    });
    return true;
  }

  /** حذف ref */
  async removeRef(blobId, refType, refId) {
    try {
      await this.store._delete(IDB_STORES.BLOB_REFS, [refType, refId, blobId]);
    } catch {}
    const metaRec = await this.store._get(IDB_STORES.BLOB_META, blobId);
    if (!metaRec) return false;
    metaRec.refCount = Math.max(0, (metaRec.refCount || 1) - 1);
    await this.store._put(IDB_STORES.BLOB_META, metaRec);
    this.releaseUrl(blobId);
    return true;
  }

  /** حذف کامل Blob */
  async delete(blobId) {
    this.releaseUrl(blobId);
    await this.store._delete(IDB_STORES.BLOBS, blobId);
    await this.store._delete(IDB_STORES.BLOB_META, blobId);
    // حذف همه‌ی refها
    const refs = await this.store._getAllByIndex(IDB_STORES.BLOB_REFS, 'blobId', blobId);
    for (const r of refs) {
      try { await this.store._delete(IDB_STORES.BLOB_REFS, [r.refType, r.refId, r.blobId]); } catch {}
    }
    this.events.emit('blob:deleted', { blobId });
  }

  /**
   * آپلود chunked برای فایل‌های بزرگ
   * با قابلیت resume
   */
  async uploadLarge(file, meta = {}, onProgress = null) {
    const uploadId = `upload_${shortId()}`;
    const totalChunks = Math.ceil(file.size / LIMITS.CHUNK_SIZE);

    this.uploadSessions.set(uploadId, {
      uploadId,
      filename: file.name,
      size: file.size,
      totalChunks,
      uploadedChunks: [],
      meta,
      startedAt: Date.now(),
    });

    await this.store._put(IDB_STORES.UPLOADS, {
      uploadId,
      filename: file.name,
      size: file.size,
      mimeType: file.type,
      totalChunks,
      uploadedChunks: [],
      meta,
      createdAt: Date.now(),
    });

    const chunks = [];
    for (let i = 0; i < totalChunks; i++) {
      const start = i * LIMITS.CHUNK_SIZE;
      const end = Math.min(start + LIMITS.CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);
      chunks.push(chunkBlob);

      // آپدیت session
      const session = this.uploadSessions.get(uploadId);
      if (session) session.uploadedChunks.push(i);

      // ذخیره‌ی پیشرفت در IDB
      await this.store._put(IDB_STORES.UPLOADS, {
        uploadId,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
        totalChunks,
        uploadedChunks: session.uploadedChunks.slice(),
        meta,
        createdAt: Date.now(),
      });

      // ✅ fake progress حذف شد — progress واقعی از save → uploadFile می‌آید
      if (onProgress) {
        // فقط ۵٪ به‌عنوان «در حال آماده‌سازی» نشون بده
        onProgress({ loaded: 0, total: file.size, percent: Math.min(5, Math.round(((i + 1) / totalChunks) * 5)) });
      }

      // yield به event loop
      if (i % 5 === 0) await sleep(0);
    }

    // ادغام
    const fullBlob = new Blob(chunks, { type: file.type });
    // ✅ onProgress رو به save پاس بده تا آپلود واقعی گزارش کنه
    const result = await this.save(fullBlob, {
      ...meta,
      filename: file.name,
      mimeType: file.type,
      onProgress,     // ← کلیدی
    });

    // پاک کردن session
    await this.store._delete(IDB_STORES.UPLOADS, uploadId);
    this.uploadSessions.delete(uploadId);

    return result;
  }

  /** بازیابی آپلودهای ناتمام */
  async listPendingUploads() {
    return this.store._getAll(IDB_STORES.UPLOADS);
  }

  /** حذف آپلود ناتمام */
  async cancelUpload(uploadId) {
    await this.store._delete(IDB_STORES.UPLOADS, uploadId);
    this.uploadSessions.delete(uploadId);
  }

  /**
   * Garbage Collection: حذف Blobهای بدون ref
   */
  async garbageCollect({ dryRun = false } = {}) {
    const allMeta = await this.store._getAll(IDB_STORES.BLOB_META);
    const now = Date.now();
    const deleted = [];
    let freedBytes = 0;

    for (const m of allMeta) {
      const hasRef = (m.refCount || 0) > 0;
      const isOld = now - (m.lastRefAt || m.uploadedAt || 0) > LIMITS.GC_BLOB_MIN_AGE_MS;
      if (!hasRef && isOld) {
        if (!dryRun) await this.delete(m.blobId);
        deleted.push(m.blobId);
        freedBytes += m.size || 0;
      }
    }

    return { deleted, freedBytes, count: deleted.length };
  }

  /** تخمین حجم کل IDB */
  async estimateSize() {
    const allMeta = await this.store._getAll(IDB_STORES.BLOB_META);
    let total = 0;
    for (const m of allMeta) total += m.size || 0;
    return total;
  }

  /** لیست همه‌ی Blobها */
  async listAll() {
    return this.store._getAll(IDB_STORES.BLOB_META);
  }

  /** پاک کردن کامل IDB (خطرناک) */
  async nuke() {
    this.clearUrlCache();
    const db = await this.store._open();
    if (!db) { this.store.memoryFallback.clear(); return; }
    const storeNames = [IDB_STORES.BLOBS, IDB_STORES.BLOB_META, IDB_STORES.BLOB_REFS, IDB_STORES.UPLOADS, IDB_STORES.META];
    for (const name of storeNames) {
      try {
        await new Promise((resolve, reject) => {
          const tx = db.transaction(name, 'readwrite');
          const req = tx.objectStore(name).clear();
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      } catch (e) { console.warn(`[nuke] ${name} failed:`, e); }
    }
  }
}

// ================================================================
// SECTION 7: QUOTA MANAGER
// ================================================================

class QuotaManager {
  constructor(localStore, blobManager, events) {
    this.local = localStore;
    this.blobs = blobManager;
    this.events = events;
    this._idbSizeCache = { value: 0, at: 0 };
  }

  /** وضعیت localStorage */
  getLocalStatus() {
    const used = this.local.estimateSize();
    const soft = LIMITS.LOCALSTORAGE_SOFT;
    const hard = LIMITS.LOCALSTORAGE_HARD;
    return {
      used,
      soft,
      hard,
      percent: Math.min(100, Math.round((used / hard) * 100)),
      level: used >= hard ? 'critical' : used >= soft ? 'warning' : 'ok',
      usedFormatted: formatBytes(used),
      hardFormatted: formatBytes(hard),
    };
  }

  /** وضعیت IndexedDB (async) */
  async getIDBStatus() {
    const now = Date.now();
    if (now - this._idbSizeCache.at < 30000) {
      const used = this._idbSizeCache.value;
      return this._buildIDBStatus(used);
    }
    const used = await this.blobs.estimateSize();
    this._idbSizeCache = { value: used, at: now };
    return this._buildIDBStatus(used);
  }

  _buildIDBStatus(used) {
    const soft = LIMITS.INDEXEDDB_SOFT;
    const hard = LIMITS.INDEXEDDB_HARD;
    return {
      used, soft, hard,
      percent: Math.min(100, Math.round((used / hard) * 100)),
      level: used >= hard ? 'critical' : used >= soft ? 'warning' : 'ok',
      usedFormatted: formatBytes(used),
      hardFormatted: formatBytes(hard),
    };
  }

  wouldExceedIDB(additionalBytes) {
    const used = this._idbSizeCache.value;
    return used + additionalBytes > LIMITS.INDEXEDDB_SOFT;
  }

  isIDBCritical() {
    return this._idbSizeCache.value >= LIMITS.INDEXEDDB_HARD;
  }

  /**
   * آزادسازی اضطراری localStorage
   * به ترتیب اولویت: audit log قدیمی، notif قدیمی، orphan قدیمی
   */
  emergencyFree(neededBytes) {
    let freed = 0;

    // ۱. AuditLog قدیمی
    const audits = this.local.get(KEYS.AUDIT_LOG, []);
    if (Array.isArray(audits) && audits.length > 100) {
      const cutoff = Date.now() - LIMITS.AUDIT_LOG_TTL_DAYS * 86400000;
      const filtered = audits.filter((a) => (a.at || 0) > cutoff);
      const removed = audits.length - filtered.length;
      if (removed > 0) {
        const before = this.local.estimateSize();
        this.local.set(KEYS.AUDIT_LOG, filtered.slice(-500));
        const after = this.local.estimateSize();
        freed += Math.max(0, before - after);
      }
    }
    if (freed >= neededBytes) return freed;

    // ۲. Notificationهای خوانده‌شده‌ی قدیمی
    const notifs = this.local.get(KEYS.NOTIFICATIONS, {});
    if (notifs && typeof notifs === 'object') {
      const cutoff = Date.now() - LIMITS.NOTIFICATION_TTL_DAYS * 86400000;
      let changed = false;
      for (const uid of Object.keys(notifs)) {
        const arr = Array.isArray(notifs[uid]) ? notifs[uid] : [];
        const filtered = arr.filter((n) => !n.read || (n.createdAt || 0) > cutoff);
        if (filtered.length !== arr.length) { notifs[uid] = filtered; changed = true; }
      }
      if (changed) {
        const before = this.local.estimateSize();
        this.local.set(KEYS.NOTIFICATIONS, notifs);
        const after = this.local.estimateSize();
        freed += Math.max(0, before - after);
      }
    }
    if (freed >= neededBytes) return freed;

    // ۳. Backup قدیمی
    const backups = this.local.get(KEYS.BACKUPS, []);
    if (Array.isArray(backups) && backups.length > LIMITS.BACKUP_KEEP_COUNT_LOW_SPACE) {
      const trimmed = backups.slice(-LIMITS.BACKUP_KEEP_COUNT_LOW_SPACE);
      const before = this.local.estimateSize();
      this.local.set(KEYS.BACKUPS, trimmed);
      const after = this.local.estimateSize();
      freed += Math.max(0, before - after);
    }

    this.events.emit('quota:emergency-free', { freed, needed: neededBytes });
    return freed;
  }

  /**
   * آزادسازی اضطراری IndexedDB
   */
  async emergencyFreeIDB(neededBytes) {
    // ۱. GC معمول
    const gc = await this.blobs.garbageCollect();
    let freed = gc.freedBytes;
    if (freed >= neededBytes) return freed;

    // ۲. آپلودهای ناتمام قدیمی
    const pending = await this.blobs.listPendingUploads();
    const now = Date.now();
    for (const p of pending) {
      if (now - (p.createdAt || 0) > 24 * 60 * 60 * 1000) {
        await this.blobs.cancelUpload(p.uploadId);
      }
    }

    // ۳. آپدیت cache
    this._idbSizeCache = { value: 0, at: 0 };
    await this.getIDBStatus();
    return freed;
  }

  /** اجرای cleanup دوره‌ای */
  async periodicCleanup() {
    const last = this.local.get(KEYS.LAST_CLEANUP, 0);
    const now = Date.now();
    if (now - last < 24 * 60 * 60 * 1000) return; // روزی یک‌بار

    // localStorage cleanup
    this.emergencyFree(0);

    // IndexedDB GC
    await this.blobs.garbageCollect();

    this.local.set(KEYS.LAST_CLEANUP, now);
    this.events.emit('quota:cleanup-done', { at: now });
  }
}

// ================================================================
// SECTION 8: MULTI-TAB SYNC
// ================================================================

class SyncManager {
  constructor(events) {
    this.events = events;
    this.tabId = shortId('tab');
    this.channel = null;
    this.storageHandler = null;
    this._init();
  }

  _init() {
    // تلاش برای BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(LIMITS.SYNC_CHANNEL);
        this.channel.onmessage = (ev) => this._handle(ev.data);
      } catch (e) {
        console.warn('[Sync] BroadcastChannel unavailable:', e);
      }
    }

    // همیشه storage event را هم گوش می‌دهیم (fallback + تکمیل)
    this.storageHandler = (ev) => {
      if (!ev.key || !ev.key.startsWith(NAMESPACE)) return;
      this.events.emit('sync:storage-event', {
        key: ev.key,
        oldValue: ev.oldValue,
        newValue: ev.newValue,
        source: 'storage',
      });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.storageHandler);
    }
  }

  _handle(msg) {
    if (!msg || msg.tabId === this.tabId) return;
    this.events.emit('sync:broadcast', msg);
    this.events.emit(`sync:${msg.type}`, msg.payload);
  }

  /** ارسال پیام به تب‌های دیگه */
  broadcast(type, payload) {
    const msg = { type, payload, tabId: this.tabId, at: Date.now() };
    if (this.channel) {
      try { this.channel.postMessage(msg); } catch {}
    }
    // storage event trick برای fallback
    if (!this.channel && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`${NAMESPACE}:__sync__`, JSON.stringify(msg));
        localStorage.removeItem(`${NAMESPACE}:__sync__`);
      } catch {}
    }
    return msg;
  }

  /** ثبت listener */
  on(type, handler) {
    return this.events.on(`sync:${type}`, handler);
  }

  /** آزادسازی */
  destroy() {
    if (this.channel) {
      try { this.channel.close(); } catch {}
      this.channel = null;
    }
    if (typeof window !== 'undefined' && this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
  }
}

// ================================================================
// SECTION 9: BACKUP MANAGER
// ================================================================

class BackupManager {
  constructor(local, events) {
    this.local = local;
    this.events = events;
  }

  /**
   * ساخت backup از کل namespace
   * نکته: video blobs خارج از backup هستند (در IDB)
   */
  create({ tag = 'manual' } = {}) {
    const keys = [
      KEYS.TEACHERS, KEYS.TEACHER_BY_USER, KEYS.TEACHER_BY_FOLDER,
      KEYS.REQUESTS, KEYS.REQUEST_IDEMPOTENCY,
      KEYS.PROJECTS, KEYS.PROJECT_BY_TEACHER, KEYS.PROJECT_BY_KEY,
      KEYS.VERSIONS, KEYS.AUDIT_LOG, KEYS.NOTIFICATIONS,
    ];
    const data = {};
    for (const k of keys) {
      data[k] = this.local.get(k, null);
    }

    const backup = {
      id: `bk_${shortId()}`,
      tag,
      schema: SCHEMA_VERSION,
      createdAt: Date.now(),
      size: 0,
      data,
    };

    const serialized = safeStringify(backup);
    backup.size = serialized ? estimateStringSize(serialized) : 0;

    // ذخیره در آرایه
    const backups = this.local.get(KEYS.BACKUPS, []) || [];
    backups.push(backup);

    // rotation
    const keep = this.local.estimateSize() > LIMITS.LOCALSTORAGE_SOFT
      ? LIMITS.BACKUP_KEEP_COUNT_LOW_SPACE
      : LIMITS.BACKUP_KEEP_COUNT;

    while (backups.length > keep) backups.shift();

    const res = this.local.set(KEYS.BACKUPS, backups);
    if (res.ok) {
      this.events.emit('backup:created', { id: backup.id, size: backup.size });
    }
    return backup;
  }

  /** لیست backupها */
  list() {
    return this.local.get(KEYS.BACKUPS, []) || [];
  }

  /** بازیابی از backup */
  restore(backupId) {
    const backups = this.list();
    const bk = backups.find((b) => b.id === backupId);
    if (!bk) return { ok: false, error: 'backup_not_found' };

    // اول snapshot فعلی برای undo
    this.create({ tag: 'pre-restore' });

    const res = this.local.transaction(async (tx) => {
      for (const [k, v] of Object.entries(bk.data)) {
        if (v == null) tx.remove(k);
        else tx.set(k, v);
      }
    });

    if (res.ok) this.events.emit('backup:restored', { id: backupId });
    return res;
  }

  /** حذف یک backup */
  delete(backupId) {
    const backups = this.list().filter((b) => b.id !== backupId);
    this.local.set(KEYS.BACKUPS, backups);
  }

  /** پاک کردن همه */
  clear() {
    this.local.set(KEYS.BACKUPS, []);
  }

  /** Export به فایل */
  export(backupId = null) {
    const data = backupId
      ? this.list().find((b) => b.id === backupId)
      : this.list();
    const str = safeStringify(data);
    return new Blob([str || '[]'], { type: 'application/json' });
  }

  /** Import از فایل */
  async import(file) {
    const text = await file.text();
    const parsed = safeParse(text, null);
    if (!parsed) return { ok: false, error: 'invalid_json' };

    if (Array.isArray(parsed)) {
      const existing = this.list();
      const merged = [...existing, ...parsed].slice(-LIMITS.BACKUP_KEEP_COUNT);
      this.local.set(KEYS.BACKUPS, merged);
      return { ok: true, count: parsed.length };
    }
    return { ok: false, error: 'invalid_format' };
  }
}

// ================================================================
// SECTION 10: MAIN ENGINE (Singleton)
// ================================================================

class StorageEngine {
  constructor() {
    this.initialized = false;
    this.events = new EventBus();
    this.local = new LocalStore();
    this._blobStore = new BlobStore();
    this.blobs = new BlobManager(this._blobStore, this.local, this.events);
    this.quota = new QuotaManager(this.local, this.blobs, this.events);
    this.sync = null;
    this.backup = new BackupManager(this.local, this.events);
    this.capabilities = {
      localStorage: hasLocalStorage(),
      indexedDB: hasIndexedDB(),
      privateMode: isPrivateMode(),
      broadcastChannel: typeof BroadcastChannel !== 'undefined',
      crypto: typeof crypto !== 'undefined' && !!crypto.subtle,
    };
  }

  async init() {
    if (this.initialized) return this;
    this.initialized = true;

    // ثبت نسخه‌ی schema
    const savedSchema = this.local.get(KEYS.SCHEMA, null);
    if (!savedSchema) {
      this.local.set(KEYS.SCHEMA, { version: SCHEMA_VERSION, installedAt: Date.now() });
    }

    // فعال‌سازی sync
    this.sync = new SyncManager(this.events);

    // periodic cleanup (بدون انتظار)
    this.quota.periodicCleanup().catch((e) => console.warn('[cleanup]', e));

    // گوش به storage events برای invalidate cache
this.events.on('sync:storage-event', ({ key }) => {
  if (key === KEYS.PROJECTS || key === KEYS.VERSIONS || key === KEYS.TEACHERS) {
    this.events.emit('data:changed', { key });
  }
});

// 🆕 اتصال به remoteSync
try {
  const { attachSyncBridge } = await import('./remoteSync.js');
  attachSyncBridge(this);
} catch (e) {
  console.warn('[StorageEngine] sync bridge not available:', e.message);
}

this.events.emit('engine:ready', { capabilities: this.capabilities });
return this;
  }

  /** آیا آماده است */
  isReady() { return this.initialized; }

  /** وضعیت کامل سیستم */
  async status() {
    const local = this.quota.getLocalStatus();
    const idb = await this.quota.getIDBStatus();
    return {
      schema: SCHEMA_VERSION,
      capabilities: this.capabilities,
      localStorage: local,
      indexedDB: idb,
      tabId: this.sync?.tabId || null,
    };
  }

  /** Reset کامل (خطرناک) */
  async resetAll({ includeBlobs = true } = {}) {
    this.local.clearNamespace();
    if (includeBlobs) await this.blobs.nuke();
    this.events.emit('engine:reset', { includeBlobs });
  }

  // ---- Proxy برای دسترسی‌های سریع ----

  // localStorage
  get(k, fb) { return this.local.get(k, fb); }
  set(k, v, opts) { return this.local.set(k, v, opts); }
  remove(k) { this.local.remove(k); }
  getRaw(k) { return this.local.getRaw(k); }
  setRaw(k, s, opts) { return this.local.setRaw(k, s, opts); }
  keys(prefix) { return this.local.keys(prefix); }
  removePrefix(prefix) { return this.local.removePrefix(prefix); }
  transaction(fn) { return this.local.transaction(fn); }

  // events
  on(ev, fn) { return this.events.on(ev, fn); }
  off(ev, fn) { this.events.off(ev, fn); }
  emit(ev, payload) { this.events.emit(ev, payload); }

  // broadcast
  broadcast(type, payload) { return this.sync?.broadcast(type, payload); }

  // 🆕 انتشار همزمان محلی + cross-tab
  // (بدون این، listenerهای محلی fire نمی‌شوند)
  broadcastAndEmit(type, payload) {
    this.events.emit(type, payload);
    return this.sync?.broadcast(type, payload);
  }

  // backup
  createBackup(opts) { return this.backup.create(opts); }
  listBackups() { return this.backup.list(); }
  restoreBackup(id) { return this.backup.restore(id); }
  exportBackup(id) { return this.backup.export(id); }
  importBackup(file) { return this.backup.import(file); }
}

// ================================================================
// SECTION 11: SINGLETON EXPORT
// ================================================================

export const engine = new StorageEngine();

// Auto-init در browser (فقط یک‌بار)
if (typeof window !== 'undefined') {
  engine.init().catch((e) => console.warn('[StorageEngine] auto-init failed:', e));

  // پاکسازی URLها هنگام بسته شدن
  window.addEventListener('beforeunload', () => {
    try { engine.blobs.clearUrlCache(); } catch {}
  });
}

// ================================================================
// SECTION 12: PUBLIC UTILITIES
// ================================================================

export const utils = {
  uuid,
  shortId,
  sha256,
  formatBytes,
  sleep,
  debounce,
  throttle,
  safeParse,
  safeStringify,
  estimateStringSize,
  hasLocalStorage,
  hasIndexedDB,
  isPrivateMode,
};

export const constants = {
  NAMESPACE,
  SCHEMA_VERSION,
  LIMITS,
  IDB_STORES,
  KEYS,
};

export default engine;